import { useEffect, useRef, useState } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import { DEFAULT_WORKING_HOURS } from "../domain/day";
import { fetchMailboxWorkingHours, fetchScheduleWorkingHours } from "../services/workingHours";
import type { WorkingHours } from "../types";

/**
 * The signed-in user's Outlook working hours, re-read every time the Calendar
 * opens so a change in Outlook shows up on the next visit. Tries mailbox
 * settings first, then free/busy (getSchedule); if neither works, the mockup's
 * Mon–Fri 09:00–17:30 stays in place.
 */
export function useWorkingHours(token: string | null): WorkingHours {
  const { refreshGraphToken } = useAuthState();
  const [hours, setHours] = useState<WorkingHours>(DEFAULT_WORKING_HOURS);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const hasToken = token !== null;

  useEffect(() => {
    const current = tokenRef.current;
    if (!current) return;

    let cancelled = false;
    // Background load: a failed refresh must not sign the user out.
    const refresh = () => refreshGraphToken(false);
    const use = (result: WorkingHours, source: string) => {
      if (cancelled) return;
      setHours(result);
      logConnectorSuccess("calendar", `load-working-hours-${source}`);
    };

    const load = async () => {
      try {
        const fromMailbox = await fetchMailboxWorkingHours(current, refresh);
        if (fromMailbox) {
          use(fromMailbox, "mailbox");
          return;
        }
      } catch (e) {
        if (!cancelled) logConnectorFailure("calendar", e, "load-working-hours-mailbox");
      }

      const email = Office.context?.mailbox?.userProfile?.emailAddress;
      if (cancelled || !email) return;
      try {
        const { hours: fromSchedule, errorCode } = await fetchScheduleWorkingHours(current, refresh, email);
        if (fromSchedule) {
          use(fromSchedule, "schedule");
        } else if (errorCode && !cancelled) {
          logConnectorFailure("calendar", new Error(`getSchedule error ${errorCode}`), "load-working-hours-schedule", {
            errorCode,
          });
        }
      } catch (e) {
        if (!cancelled) logConnectorFailure("calendar", e, "load-working-hours-schedule");
      }
    };
    void load();

    return () => {
      cancelled = true;
    };
  }, [hasToken, refreshGraphToken]);

  return hours;
}
